import React from "react";
import PropTypes from "prop-types";
import { Card, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Flex from "components/common/Flex";
import { PieChart } from "echarts/charts";
import * as echarts from "echarts/core";
import BasicECharts from "components/common/BasicEChart";
import {
  GridComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useAppContext } from "providers/AppProvider";
import { useSelector } from "react-redux";

// Register ECharts modules
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer,
]);

// Status styles - matching main dashboard colors
const statusConfig = {
  error: { color: "#f5803e" },
  Error: { color: "#f5803e" },
  invalid: { color: "#ed5e0b" },
  "no response": { color: "#26bcfc" },
  approved: { color: "#2c7ce6" },
  "ready to submit": { color: "#2c7ce6" },
  "under progress": { color: "#d16e34" },
  rejected: { color: "#ed5e0b" },
  cancelled: { color: "#269dd0" },
  partial: { color: "#0da9bd" },
  pended: { color: "#d16e34" },
  "not-required": { color: "#197aa4" },
  queued: { color: "#2a91ed" },
  adjudication: { color: "#b8571f" },
  prepared: { color: "#a9e2fd" },
  "validation-error": { color: "#ed5e0b" },
};

// Define status mappings to match data keys
const statusMappings = [
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "partial", label: "Partially Approved" },
  { key: "pended", label: "Pended" },
  { key: "noresp", label: "No Response" },
  { key: "error", label: "Error" },
  { key: "Ready_to_submit", label: "Ready to Submit" },
  { key: "invalid", label: "Invalid" },
  { key: "validation-error", label: "Validation Error" },
  { key: "cancel", label: "Cancelled" },
  { key: "queued", label: "Queued" },
  { key: "adjudication", label: "Adjudication" },
  { key: "not-required", label: "Not Required" },
];

const radius = ["50%", "70%"];

const getOptions = (getThemeColor, data, radius, total) => ({
  color: data.map(
    (item) => statusConfig[item.name.toLowerCase()]?.color || "#ccc"
  ),
  tooltip: {
    padding: [7, 10],
    transitionDuration: 0,
    backgroundColor: getThemeColor("gray-100"),
    borderColor: getThemeColor("gray-300"),
    textStyle: { color: getThemeColor("gray-1100") },
    borderWidth: 1,
    formatter: (params) =>
      `<strong>${params.data.name}:</strong> ${params.percent}%`,
  },
  series: [
    {
      name: "Total Claims",
      type: "pie",
      radius,
      avoidLabelOverlap: false,
      emphasis: { scale: false },
      itemStyle: {
        borderWidth: 2,
        borderColor: getThemeColor("gray-100"),
      },
      label: {
        show: true,
        position: "center",
        fontSize: 13,
        formatter: `{a| ${total}}`,
        rich: {
          a: {
            fontSize: 14,
            color: getThemeColor("gray-1100"),
            lineHeight: 15,
          },
        },
      },
      data,
    },
  ],
});

// Legend item component
const ClaimStatusItem = ({ item, total }) => {
  const config = statusConfig[item.name.toLowerCase()] || {};
  const percentage = total ? ((item.value * 100) / total).toFixed(0) : 0;

  return (
    <Flex
      alignItems="center"
      justifyContent="between"
      className="fw-semibold fs-11 mb-1"
    >
      <p className="mb-0 d-flex align-items-center text-capitalize">
        <FontAwesomeIcon
          icon="circle"
          className="me-2"
          style={{ color: config.color || "#ccc" }}
        />
        {item.name}
      </p>
      <div>{percentage}%</div>
    </Flex>
  );
};

ClaimStatusItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
  }).isRequired,
  total: PropTypes.number.isRequired,
};

const ClaimsDistributionByStatus = () => {
  const { getThemeColor } = useAppContext();
  const { claimDashboardData } = useSelector((state) => state.claimDashboard);
  const claimData = claimDashboardData?.claim || {};

  // Generate claims data from claim object
  const claimsData = statusMappings.map(({ key, label }) => ({
    name: label,
    value: claimData[key]?.total || 0,
  }));

  // Calculate total claims
  const total = claimsData.reduce((sum, val) => sum + val.value, 0);

  return (
    <Col xs={12} md={6}>
      <div className="card h-100 shadow-sm">
        <div className="card-body">
          <h5 className="fs-9 text-primary fw-normal">
            Claims Distribution by Status
          </h5>

          <Row className="align-items-center">
            {/* Legend */}
            <Col xs={12} md={7}>
              <Row>
                <Col xs={6}>
                  {claimsData
                    .slice(0, Math.ceil(claimsData.length / 2))
                    .map((item, i) => (
                      <ClaimStatusItem key={i} item={item} total={total} />
                    ))}
                </Col>
                <Col xs={6}>
                  {claimsData
                    .slice(Math.ceil(claimsData.length / 2))
                    .map((item, i) => (
                      <ClaimStatusItem
                        key={i + Math.ceil(claimsData.length / 2)}
                        item={item}
                        total={total}
                      />
                    ))}
                </Col>
              </Row>
            </Col>

            {/* Chart */}
            <Col xs={12} md={5} className="text-center">
              <BasicECharts
                echarts={echarts}
                options={getOptions(getThemeColor, claimsData, radius, total)}
                style={{ width: "12rem", height: "12rem", margin: "0 auto" }}
              />
            </Col>
          </Row>
        </div>
      </div>
    </Col>
  );
};

export default ClaimsDistributionByStatus;
